import { Stack, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import { DateFormatOptions, getDateFromEvent, getDateString } from 'common';
import React, { FC, ForwardedRef, RefObject, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { DeviceType, useBreakpoint } from '../../../hooks/useBreakpoint';
import { CfTextField, CfTextFieldProps } from '../../TextField/CfTextField';
import { CfDatePicker } from '../DatePicker/CfDatePicker';
import { StyledDatePickerErrorTypography } from './DateRangePicker.styles';

/** placement values related to an automatic distribution */
type AutoPlacement = 'auto' | 'auto-start' | 'auto-end';

/** placement values related to position */
type BasePlacement = 'top' | 'bottom' | 'right' | 'left';

/** placement value matrix of position and justification */
type VariationPlacement =
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'right-start'
  | 'right-end'
  | 'left-start'
  | 'left-end';

/** placement values relate to position and justification */
export type Placement = AutoPlacement | BasePlacement | VariationPlacement;

/**
 * describes how the CfMultipleDatePicker component should present and behave
 */
export interface CfMultipleDatePickerProps {
  onCalendarOpen?: () => void;
  onEndDateCalendarClose?: () => void;
  onChange?: (startDate: Date, endDate: Date) => void;
  startDate?: Date | null;
  endDate?: Date | null;
  startDatePickerPlacement?: Placement | undefined;
  endDatePickerPlacement?: Placement | undefined;
  minDate?: Date;
  maxDate?: Date;
  startDateLabel?: string;
  endDateLabel?: string;
  sx?: SxProps<Theme>;
  inputSx?: SxProps<Theme>;
  hasError?: boolean;
  disabled?: boolean;
  handleHasError?: () => void;
  errorText?: string;
  startDateInputTestId?: string;
  endDateInputTestId?: string;
  errorSx?: SxProps<Theme>;
  startDateRef?: RefObject<HTMLInputElement>;
  preventOpenCalendarOnFocus?: boolean;
}

/**
 * @param onCalendarOpen - handles the onCalendarOpen event of the start and end date's CfDatePicker components
 * @param onChange - handles the onChange event of the start and end date's CfDatePicker components
 * @param startDate - the initial value passed to the respective CfTextField and CfDatePicker components
 * @param endDate -  the initial value passed to the respective CfTextField and CfDatePicker components
 * @param startDatePickerPlacement - passed to the start date's CfDatePicker component's popperPlacement property
 * @param endDatePickerPlacement -  passed to the end date's CfDatePicker component's popperPlacement property
 * @param minDate - passed to the start and end date's CfDatePicker components' minDate property
 * @param maxDate - passed to the start and end date's CfDatePicker components' maxDate property
 * @param startDateLabel - passed to the start date's CfTextField component
 * @param endDateLabel - passed to the end date's CfTextField component
 * @param sx - passed to the component containing the CfDatePicker components (not the error text components)
 * @param inputSx - passed to the start and end date's CfTextField component
 * @param hasError -  passed to the start and end date's CfTextField components' error property and determines whether to render the errorText value
 * @param disabled - passed to the start and end date's CfTextField components and CfDatePicker components
 * @param handleHasError - called when either CfTextField components' value changes
 * @param errorText - the error message that is displayed after both CfDatePicker components
 * @param startDateInputTestId - passed to the start date's CfTextField component's data-testId property
 * @param endDateInputTestId - passed to the end date's CfTextField component's data-testId property
 * @param  errorSx - styles the errorText
 */
export const CfMultipleDatePicker: FC<CfMultipleDatePickerProps> = (props: CfMultipleDatePickerProps) => {
  const { deviceType } = useBreakpoint();

  const [startDate, setStartDate] = useState<Date | null | undefined>(props.startDate);
  const [endDate, setEndDate] = useState<Date | null | undefined>(props.endDate);

  const [startDateError, setStartDateError] = useState<boolean>(false);
  const [endDateError, setEndDateError] = useState<boolean>(false);

  let startDateCalendar: DatePicker<never> | null;
  let endDateCalendar: DatePicker<never> | null;

  const dateRangeError = startDate && endDate && startDate > endDate;

  useEffect(() => {
    if (props.startDate != startDate) {
      setStartDate(props.startDate);
    }
    if (props.endDate != endDate) {
      setEndDate(props.endDate);
    }
  }, [props.startDate, props.endDate]);

  const setBoundedStartDate = (date?: Date | null) => {
    if (date) {
      if (props.minDate && date < props.minDate) return;
      else if (props.maxDate && date > props.maxDate) return;
      if (date.toDateString() !== startDate?.toDateString()) {
        setStartDate(date);

        if (endDate && date <= endDate) {
          props.onChange?.(date, endDate);
        }
      }
    }
  };

  const setBoundedEndDate = (date?: Date | null) => {
    if (date) {
      if (props.minDate && date < props.minDate) return;
      else if (props.maxDate && date > props.maxDate) return;
      if (date.toDateString() !== endDate?.toDateString()) {
        setEndDate(date);

        if (startDate && startDate <= date) {
          props.onChange?.(startDate, date);
        }
      }
    }
  };

  const handleStartDateCalendarInputChange = (date: Date | null) => {
    setBoundedStartDate(date);
  };

  const handleEndDateCalendarInputChange = (date: Date | null) => {
    setBoundedEndDate(date);
  };

  const validateAndGetDate = (
    e:
      | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
      | React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
    type: 'start' | 'end'
  ): Date | null => {
    const date = getDateFromEvent(e);

    // valid: set error to false
    if (date) {
      if (type === 'start') {
        setStartDateError(false);
      } else if (type === 'end') {
        setEndDateError(false);
      }
    }

    // invalid: set error to true and date to null
    else {
      if (type === 'start') {
        setStartDateError(true);
        setStartDate(null);
      } else if (type === 'end') {
        setEndDateError(true);
        setEndDate(null);
      }
      props.handleHasError?.();
    }

    return date;
  };

  const handleStartDateTextInputBlur = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (!e.currentTarget?.value) return;
    const date = validateAndGetDate(e, 'start');
    if (date) {
      handleStartDateCalendarInputChange(date);
      evaluateStartDatePickerFocus();
    }
  };

  const handleEndDateTextInputBlur = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (!e.currentTarget?.value) return;
    const date = validateAndGetDate(e, 'end');
    if (date) {
      handleEndDateCalendarInputChange(date);
      evaluateEndDatePickerFocus(null);
    }
  };

  const handleStartDateTextInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'NumpadEnter') {
      const date = validateAndGetDate(e, 'start');
      if (date) handleStartDateCalendarInputChange(date);
    }
  };

  const handleEndDateTextInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'NumpadEnter') {
      const date = validateAndGetDate(e, 'end');
      if (date) handleEndDateCalendarInputChange(date);
    }
  };

  const evaluateStartDatePickerFocus = () => {
    // handles inconsistent onblur behavior for mobile & desktop safari
    if (deviceType !== DeviceType.Desktop || (/Safari/i.test(navigator.userAgent) && /Apple/.test(navigator.vendor)))
      return;

    setTimeout(
      () =>
        !document.getElementById('cf-start-date-input')?.matches(':focus-within') &&
        !document.getElementById('cf-start-date-picker')?.matches(':focus-within') &&
        startDateCalendar?.setOpen(false),
      200
    );
  };

  const evaluateEndDatePickerFocus = (event: React.FocusEvent<HTMLDivElement> | null) => {
    // handles inconsistent onblur behavior for mobile & desktop safari
    if (/Safari/i.test(navigator.userAgent) && /Apple/.test(navigator.vendor)) return;

    setTimeout(() => {
      if (
        !document.getElementById('cf-end-date-input')?.matches(':focus-within') &&
        !document.getElementById('cf-end-date-picker')?.matches(':focus-within')
      ) {
        endDateCalendar?.setOpen(false);
        if (event) {
          props.onEndDateCalendarClose?.();
        }
      }
    }, 200);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const customStartDateInput = (custInputProps: CfTextFieldProps, _ref: ForwardedRef<HTMLElement>) => {
    return (
      <CfTextField
        {...custInputProps}
        inputRef={props.startDateRef}
        sx={{ width: 89, ...props.inputSx }}
        type='text'
        inputLabel={props.startDateLabel}
        defaultValue={getDateString(startDate, DateFormatOptions)}
        value={undefined}
        error={dateRangeError || startDateError || props.hasError}
        disabled={props.disabled}
        data-testid={props.startDateInputTestId}
        onChange={undefined}
        onClick={(e) => {
          e.preventDefault();
          startDateCalendar?.setOpen(true);
        }}
        onFocus={(e) => {
          e.preventDefault();
          startDateCalendar?.setOpen(props.preventOpenCalendarOnFocus ? !props.preventOpenCalendarOnFocus : true);
        }}
        onKeyDown={handleStartDateTextInputKeyDown}
        onBlur={handleStartDateTextInputBlur}
      />
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const customEndDateInput = (custInputProps: CfTextFieldProps, _ref: ForwardedRef<HTMLElement>) => {
    return (
      <CfTextField
        {...custInputProps}
        sx={{ width: 89, ...props.inputSx }}
        type='text'
        inputLabel={props.endDateLabel}
        defaultValue={getDateString(endDate, DateFormatOptions)}
        value={undefined}
        error={dateRangeError || endDateError || props.hasError}
        disabled={props.disabled}
        data-testid={props.endDateInputTestId}
        onChange={undefined}
        onKeyDown={handleEndDateTextInputKeyDown}
        onBlur={handleEndDateTextInputBlur}
        onClick={(e) => {
          e.preventDefault();
          endDateCalendar?.setOpen(true);
        }}
        onFocus={(e) => {
          e.preventDefault();
          endDateCalendar?.setOpen(true);
        }}
      />
    );
  };

  return (
    <>
      <Stack direction='row' gap={1} sx={props.sx}>
        <CfDatePicker
          id='cf-start-date-input'
          calendarId='cf-start-date-picker'
          popperPlacement={props.startDatePickerPlacement}
          customInput={React.createElement(React.forwardRef(customStartDateInput))}
          selected={startDate}
          maxDate={props.maxDate}
          minDate={props.minDate}
          onCalendarContainerBlur={evaluateStartDatePickerFocus}
          onChange={handleStartDateCalendarInputChange}
          onCalendarOpen={props.onCalendarOpen}
          calendarRef={(c) => (startDateCalendar = c)}
          disabled={props.disabled}
        />
        <CfDatePicker
          id='cf-end-date-input'
          calendarId='cf-end-date-picker'
          popperPlacement={props.endDatePickerPlacement}
          customInput={React.createElement(React.forwardRef(customEndDateInput))}
          selected={endDate}
          maxDate={props.maxDate}
          minDate={startDate}
          onCalendarContainerBlur={evaluateEndDatePickerFocus}
          onChange={handleEndDateCalendarInputChange}
          onCalendarOpen={props.onCalendarOpen}
          calendarRef={(c) => (endDateCalendar = c)}
          disabled={props.disabled}
        />
      </Stack>
      {dateRangeError && (
        <StyledDatePickerErrorTypography sx={props.errorSx}>
          Start date must be prior to end date
        </StyledDatePickerErrorTypography>
      )}
      {(startDateError || endDateError || props.hasError) && (
        <StyledDatePickerErrorTypography sx={props.errorSx}>
          {props.errorText ? props.errorText : 'Please enter valid date'}
        </StyledDatePickerErrorTypography>
      )}
    </>
  );
};
